(() => {
  if (window.ZDOffersQuick) return;
  const records = new WeakMap();
  let queue = Promise.resolve();

  function rootUrl() {
    const value = window.Shopify?.routes?.root || document.querySelector('zd-offer-dialog')?.dataset.rootUrl || '/';
    return value.endsWith('/') ? value : value + '/';
  }

  function cartUrl() {
    return document.querySelector('zd-offer-dialog')?.dataset.cartUrl || rootUrl() + 'cart';
  }

  function messages() {
    return document.querySelector('zd-offer-dialog')?.messages || {};
  }

  function failure(key, extra = {}) {
    return Object.assign(new Error(messages()[key] || ''), { key }, extra);
  }

  function active(deadline) {
    if (!Number(deadline)) return true;
    return window.ZDFlash?.isActive ? window.ZDFlash.isActive(Number(deadline)) : Number(deadline) * 1000 > Date.now();
  }

  function serialize(work) {
    const result = queue.then(work, work);
    queue = result.catch(() => {});
    return result;
  }

  async function request(path, body) {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 12000);
    try {
      const response = await fetch(rootUrl() + path, {
        method: body === undefined ? 'GET' : 'POST',
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { Accept: 'application/json', ...(body === undefined ? {} : { 'Content-Type': 'application/json' }) },
        body: body === undefined ? undefined : JSON.stringify(body),
        signal: controller.signal
      });
      const data = await response.json();
      if (!response.ok || Number(data.status) >= 400) {
        throw failure('add_error', { status: response.status, serverMessage: typeof data.description === 'string' ? data.description : '' });
      }
      return data;
    } finally {
      window.clearTimeout(timeout);
    }
  }

  async function getCart() {
    try {
      const cart = await request('cart.js');
      if (!Array.isArray(cart.items)) throw failure('cart_unavailable');
      return cart;
    } catch (_) {
      throw failure('cart_unavailable');
    }
  }

  function discountCodes(cart) {
    const codes = new Map();
    const remember = value => {
      if (typeof value === 'string' && value.trim()) codes.set(value.trim().toLowerCase(), value.trim());
    };
    (cart.discount_codes || []).forEach(item => remember(typeof item === 'string' ? item : item.code));
    const applications = [...(cart.cart_level_discount_applications || [])];
    (cart.items || []).forEach(item => {
      (item.line_level_discount_allocations || []).forEach(allocation => applications.push(allocation.discount_application));
    });
    applications.forEach(application => {
      if (application?.type === 'discount_code') remember(application.code || application.title);
    });
    return [...codes.values()];
  }

  function discountApplied(cart, code) {
    const wanted = code.toLowerCase();
    if ((cart.discount_codes || []).some(item => typeof item === 'object' && item.code?.toLowerCase() === wanted && item.applicable === true)) return true;
    const applications = [...(cart.cart_level_discount_applications || [])];
    (cart.items || []).forEach(item => {
      (item.line_level_discount_allocations || []).forEach(allocation => applications.push(allocation.discount_application));
    });
    return applications.some(application => application?.type === 'discount_code' && (application.code || application.title || '').toLowerCase() === wanted);
  }

  async function applyDiscountNow(code, deadline, currentCart) {
    let cart = currentCart || await getCart();
    if (!code) return cart;
    if (!active(deadline)) throw failure('expired', { cart });
    if (code.includes(',')) throw failure('discount_not_applied', { cart });
    if (discountApplied(cart, code)) return cart;
    const codes = discountCodes(cart);
    if (!codes.some(value => value.toLowerCase() === code.toLowerCase())) codes.push(code);
    try {
      cart = await request('cart/update.js', { discount: codes.join(',') });
    } catch (_) {
      try {
        cart = await getCart();
      } catch (_) {
        throw failure('discount_not_applied', { cart });
      }
    }
    if (!active(deadline)) throw failure('expired', { cart });
    if (!discountApplied(cart, code)) throw failure('discount_not_applied', { cart });
    return cart;
  }

  function recordFor(card) {
    if (!records.has(card)) records.set(card, { added: 0, uncertain: false, busy: false });
    return records.get(card);
  }

  function variantQuantity(cart, variant) {
    return (cart.items || []).filter(item => String(item.variant_id || item.id) === String(variant) && !item.selling_plan_allocation)
      .reduce((sum, item) => sum + Number(item.quantity || 0), 0);
  }

  function addSelection(card, quantity) {
    const record = recordFor(card);
    return serialize(async () => {
      if (record.uncertain) throw failure('unknown_add', { uncertain: true });
      if (!active(card.dataset.offerEnd)) throw failure('expired');
      let cart = await getCart();
      const variant = card.dataset.offerVariant;
      const before = variantQuantity(cart, variant);
      record.added = Math.min(record.added, before);
      const extra = Math.max(0, quantity - record.added);
      if (extra > 0) {
        let accepted = false;
        let addError;
        try {
          const result = await request('cart/add.js', { items: [{ id: variant, quantity: extra }] });
          accepted = Array.isArray(result.items) && result.items.some(item => String(item.variant_id || item.id) === variant);
          if (!accepted) throw failure('unknown_add');
        } catch (error) {
          addError = error;
        }
        try {
          cart = await getCart();
        } catch (_) {
          if (accepted) record.added += extra;
          else record.uncertain = true;
          throw failure(accepted ? 'discount_not_applied' : 'unknown_add', { uncertain: !accepted });
        }
        const difference = Math.max(0, variantQuantity(cart, variant) - before);
        if (accepted) {
          record.added += extra;
        } else if (difference >= extra) {
          record.added += extra;
        } else if (difference > 0) {
          record.added += difference;
          throw failure('add_error', { cart, added: true, serverMessage: addError?.serverMessage || '' });
        } else if (addError?.status >= 400 && addError.status < 500) {
          throw addError;
        } else {
          record.uncertain = true;
          throw failure('unknown_add', { cart, uncertain: true });
        }
      }
      try {
        return await applyDiscountNow(card.dataset.offerDiscountCode || '', card.dataset.offerEnd, cart);
      } catch (error) {
        error.added = record.added > 0;
        throw error;
      }
    });
  }

  async function notifyCart(cart, variant, openDrawer, trigger) {
    document.dispatchEvent(new CustomEvent('zd:offer-cart-updated', { detail: { cart, variantId: variant } }));
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'zd-offers-quick', productVariantId: variant, cartData: cart });
    }
    const drawer = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    if (!openDrawer || !drawer?.getSectionsToRender || !drawer.renderContents) return;
    try {
      const ids = drawer.getSectionsToRender().map(section => section.id).slice(0, 5);
      const sections = await request('?sections=' + encodeURIComponent(ids.join(',')));
      if (!ids.every(id => typeof sections[id] === 'string')) return;
      const item = cart.items.find(item => String(item.variant_id || item.id) === String(variant));
      drawer.setActiveElement?.(trigger);
      drawer.classList.remove('is-empty');
      drawer.renderContents({ ...cart, ...item, sections });
    } catch (_) {}
  }

  class OfferActions extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.card = this.closest('[data-zd-offer-card]');
      this.form = this.querySelector('[data-zd-offer-form]');
      this.add = this.querySelector('[data-zd-offer-add]');
      this.buy = this.querySelector('[data-zd-offer-buy]');
      this.status = this.querySelector('[data-zd-offer-status]');
      this.cartLink = this.querySelector('[data-zd-offer-cart-link]');
      if (!this.card || !this.form || !this.add) return;
      this.originalLabel = this.add.textContent;
      this.form.addEventListener('submit', event => {
        if (!document.querySelector('zd-offer-dialog')?.messages) return;
        event.preventDefault();
        this.addToCart();
      });
      this.buy?.setAttribute('aria-haspopup', 'dialog');
      this.buy?.setAttribute('aria-controls', 'ZdOfferDialog');
      this.buy?.addEventListener('click', event => {
        const modal = document.querySelector('zd-offer-dialog');
        if (!modal?.openFor || !modal.dialog?.showModal || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        if (recordFor(this.card).busy) return;
        modal.openFor(this.card, this.buy);
      });
    }

    showStatus(message, error, cartLink) {
      this.status.textContent = message;
      this.status.hidden = !message;
      this.status.dataset.error = String(Boolean(error));
      this.cartLink.hidden = !cartLink;
    }

    async addToCart() {
      const record = recordFor(this.card);
      if (record.busy) return;
      if (record.uncertain) {
        window.location.assign(cartUrl());
        return;
      }
      record.busy = true;
      this.add.disabled = true;
      this.buy?.setAttribute('aria-disabled', 'true');
      this.add.textContent = messages().adding;
      this.form.setAttribute('aria-busy', 'true');
      this.showStatus('', false, false);
      try {
        if (record.added > 0) {
          const currentCart = await getCart();
          record.added = Math.min(record.added, variantQuantity(currentCart, this.card.dataset.offerVariant));
          if (record.added > 0) {
            window.location.assign(cartUrl());
            return;
          }
        }
        const quantity = Number(this.form.elements.quantity.value);
        if (!Number.isSafeInteger(quantity) || quantity < 1) throw failure('quantity_error');
        const cart = await addSelection(this.card, quantity);
        this.showStatus(messages().added, false, true);
        void notifyCart(cart, this.card.dataset.offerVariant, true, this.add);
      } catch (error) {
        this.showStatus(error.serverMessage || messages()[error.key] || messages().add_error, true, Boolean(record.added || record.uncertain));
        if (error.cart) void notifyCart(error.cart, this.card.dataset.offerVariant, false, this.add);
      } finally {
        record.busy = false;
        this.add.disabled = false;
        this.buy?.removeAttribute('aria-disabled');
        this.form.removeAttribute('aria-busy');
        this.add.textContent = record.added || record.uncertain ? messages().view_cart : this.originalLabel;
      }
    }
  }

  class OfferDialog extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.dialog = this.querySelector('dialog');
      try {
        this.messages = JSON.parse(this.querySelector('[data-zd-offer-messages]').textContent);
      } catch (_) {
        return;
      }
      this.quantity = this.querySelector('[data-zd-dialog-quantity]');
      this.checkout = this.querySelector('[data-zd-dialog-checkout]');
      this.status = this.querySelector('[data-zd-dialog-status]');
      this.cartLink = this.querySelector('[data-zd-dialog-cart]');
      this.existing = this.querySelector('[data-zd-dialog-existing]');
      this.querySelector('[data-zd-offer-close]').addEventListener('click', () => this.dialog.close());
      this.dialog.addEventListener('click', event => {
        if (event.target !== this.dialog) return;
        const rect = this.dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.dialog.close();
      });
      this.dialog.addEventListener('close', () => {
        this.openToken = null;
        if (this.trigger?.isConnected && this.trigger.getClientRects().length) this.trigger.focus({ preventScroll: true });
      });
      this.quantity.addEventListener('input', () => this.updateSubtotal());
      this.checkout.addEventListener('click', () => this.proceed());
      this.expirationListener = () => {
        if (this.dialog.open && this.card && !active(this.card.dataset.offerEnd)) this.expire();
      };
      document.addEventListener('zd:flash-expired', this.expirationListener);
    }

    disconnectedCallback() {
      if (this.expirationListener) document.removeEventListener('zd:flash-expired', this.expirationListener);
    }

    showStatus(message, cartLink = false) {
      this.status.textContent = message;
      this.status.hidden = !message;
      this.cartLink.hidden = !cartLink;
    }

    async openFor(card, trigger) {
      if (this.busy || recordFor(card).busy) return;
      this.card = card;
      this.trigger = trigger;
      this.openToken = {};
      const token = this.openToken;
      const record = recordFor(card);
      this.querySelector('[data-zd-dialog-title]').textContent = card.dataset.offerTitle;
      this.querySelector('[data-zd-dialog-price]').textContent = card.dataset.offerPriceText;
      const compare = this.querySelector('[data-zd-dialog-compare]');
      compare.textContent = card.dataset.offerRegularPriceText;
      compare.hidden = card.dataset.offerPrice === card.dataset.offerRegularPrice;
      this.querySelector('[data-zd-dialog-product-link]').href = card.dataset.offerUrl;
      const image = card.querySelector('.zd-shop-card__image img');
      const imageContainer = this.querySelector('[data-zd-dialog-image]');
      imageContainer.replaceChildren();
      if (image) {
        const clone = image.cloneNode();
        clone.removeAttribute('id');
        clone.removeAttribute('loading');
        clone.sizes = '110px';
        imageContainer.append(clone);
      }
      this.quantity.disabled = false;
      this.quantity.min = String(Math.max(Number(card.dataset.offerQuantityMin) || 1, record.added));
      this.quantity.step = card.dataset.offerQuantityStep || '1';
      if (card.dataset.offerQuantityMax) this.quantity.max = card.dataset.offerQuantityMax;
      else this.quantity.removeAttribute('max');
      this.quantity.value = this.quantity.min;
      this.checkout.disabled = false;
      this.checkout.textContent = this.messages.continue_to_checkout;
      this.existing.hidden = true;
      this.showStatus('');
      this.updateSubtotal();
      this.dialog.showModal();
      if (!active(card.dataset.offerEnd)) {
        this.expire();
        return;
      }
      if (record.uncertain) {
        this.showStatus(this.messages.unknown_add, true);
        this.checkout.disabled = true;
        return;
      }
      try {
        const cart = await getCart();
        if (this.openToken === token) this.existing.hidden = cart.item_count === 0;
      } catch (_) {
        if (this.openToken === token) this.showStatus(this.messages.cart_unavailable);
      }
    }

    updateSubtotal() {
      const quantity = Number(this.quantity.value);
      if (!this.card || !Number.isFinite(quantity) || quantity < 1) return;
      const cents = Number(this.card.dataset.offerPrice) * quantity;
      let display;
      try {
        display = new Intl.NumberFormat(document.documentElement.lang || 'en', {
          style: 'currency', currency: this.card.dataset.offerCurrency
        }).format(cents / 100);
      } catch (_) {
        display = this.card.dataset.offerPriceText;
      }
      this.querySelector('[data-zd-dialog-subtotal]').textContent = display;
    }

    expire() {
      this.checkout.disabled = true;
      this.quantity.disabled = true;
      this.showStatus(this.messages.expired, recordFor(this.card).added > 0);
      this.querySelector('[data-zd-dialog-product-link]').href = this.card.dataset.offerProductUrl;
    }

    async proceed() {
      if (this.busy || !this.card) return;
      const record = recordFor(this.card);
      if (record.busy) return;
      if (!active(this.card.dataset.offerEnd)) {
        this.expire();
        return;
      }
      const quantity = Number(this.quantity.value);
      if (!this.quantity.checkValidity() || !Number.isSafeInteger(quantity) || quantity < 1) {
        this.showStatus(this.messages.quantity_error);
        this.quantity.reportValidity();
        return;
      }
      this.busy = true;
      record.busy = true;
      const token = this.openToken;
      const card = this.card;
      this.checkout.disabled = true;
      this.quantity.disabled = true;
      this.checkout.textContent = this.messages.checkout_loading;
      this.dialog.setAttribute('aria-busy', 'true');
      this.showStatus('');
      let navigating = false;
      try {
        const cart = await addSelection(card, quantity);
        void notifyCart(cart, card.dataset.offerVariant, false, this.trigger);
        if (!active(card.dataset.offerEnd)) {
          this.expire();
          return;
        }
        if (this.openToken === token && this.dialog.open) {
          navigating = true;
          this.dialog.close();
          window.location.assign(this.dataset.checkoutUrl);
        }
      } catch (error) {
        if (this.openToken === token) {
          if (record.added > 0 && !record.uncertain) {
            this.quantity.value = String(record.added);
            this.quantity.min = String(Math.max(Number(card.dataset.offerQuantityMin) || 1, record.added));
            this.updateSubtotal();
          }
          this.showStatus(error.serverMessage || this.messages[error.key] || this.messages.add_error, Boolean(record.added || record.uncertain));
        }
        if (error.cart) void notifyCart(error.cart, card.dataset.offerVariant, false, this.trigger);
      } finally {
        this.busy = false;
        record.busy = false;
        if (!navigating) {
          this.checkout.disabled = record.uncertain || !active(card.dataset.offerEnd);
          this.quantity.disabled = record.uncertain || !active(card.dataset.offerEnd);
          this.checkout.textContent = this.messages.continue_to_checkout;
          this.dialog.removeAttribute('aria-busy');
          const actions = card.querySelector('zd-offer-actions');
          if (record.added > 0 && actions?.add) actions.add.textContent = this.messages.view_cart;
        }
      }
    }
  }

  window.ZDOffersQuick = {
    applyDiscount: (code, deadline) => serialize(() => applyDiscountNow(String(code || ''), deadline)),
    getCart,
    refreshCart: (cart, variantId) => notifyCart(cart, variantId, false),
    isActive: active
  };
  if (!customElements.get('zd-offer-dialog')) customElements.define('zd-offer-dialog', OfferDialog);
  if (!customElements.get('zd-offer-actions')) customElements.define('zd-offer-actions', OfferActions);
})();
